import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  createDialuxTask, getDialuxTask, listDialuxTasks,
  createDialuxComparison, getDialuxComparison, getDialuxAgentStatus
} from '../lib/api';
import {
  Lightbulb, Plus, Clock, CheckCircle, XCircle, AlertTriangle, Loader2,
  Building2, Ruler, Zap, Sun, Eye, BarChart3, FileText, RefreshCw,
  Wifi, WifiOff, ArrowRight, Copy, Download
} from 'lucide-react';

const t = (lang, pl, en) => lang === 'pl' ? pl : en;

const ROOM_TYPES = [
  { key: 'office_general', label: 'Biuro ogolne', lux: 500, ugr: 19 },
  { key: 'office_design', label: 'Biuro projektowe / CAD', lux: 750, ugr: 16 },
  { key: 'conference_room', label: 'Sala konferencyjna', lux: 500, ugr: 19 },
  { key: 'reception', label: 'Recepcja / hol', lux: 300, ugr: 22 },
  { key: 'corridor', label: 'Korytarz', lux: 100, ugr: 25 },
  { key: 'warehouse', label: 'Magazyn', lux: 200, ugr: 25 },
  { key: 'production_general', label: 'Hala produkcyjna', lux: 300, ugr: 25 },
  { key: 'production_precision', label: 'Hala precyzyjna', lux: 750, ugr: 19 },
  { key: 'workshop', label: 'Warsztat', lux: 500, ugr: 22 },
  { key: 'restaurant', label: 'Restauracja', lux: 200, ugr: 22 },
  { key: 'hotel_room', label: 'Pokoj hotelowy', lux: 300, ugr: 19 },
  { key: 'hotel_lobby', label: 'Lobby hotelowe', lux: 300, ugr: 22 },
  { key: 'shop_general', label: 'Sklep', lux: 300, ugr: 22 },
  { key: 'shop_premium', label: 'Sklep premium', lux: 500, ugr: 19 },
  { key: 'classroom', label: 'Sala lekcyjna', lux: 300, ugr: 19 },
  { key: 'lecture_hall', label: 'Sala wykladowa', lux: 500, ugr: 19 },
  { key: 'hospital_room', label: 'Pokoj szpitalny', lux: 300, ugr: 19 },
  { key: 'hospital_exam', label: 'Gabinet lekarski', lux: 500, ugr: 19 },
  { key: 'parking_indoor', label: 'Parking podziemny', lux: 75, ugr: 25 },
];

export default function DialuxProjects({ lang, tenantId }) {
  var [tasks, setTasks] = useState([]);
  var [stats, setStats] = useState({});
  var [agentStatus, setAgentStatus] = useState({});
  var [showNew, setShowNew] = useState(false);
  var [showCompare, setShowCompare] = useState(false);
  var [selectedTask, setSelectedTask] = useState(null);
  var [loading, setLoading] = useState(false);

  // New task form
  var [form, setForm] = useState({
    room_type: 'office_general',
    room_name: '',
    length_m: 10,
    width_m: 8,
    height_m: 2.8,
    luminaire_name: '',
    luminaire_manufacturer: '',
    luminaire_power_w: 36,
    luminaire_lumens: 3600,
    luminaire_cct_k: 4000,
    budget_pln: 0,
    client_name: '',
    notes: '',
  });

  // Comparison form
  var [compareForm, setCompareForm] = useState({
    room_type: 'office_general',
    room_name: '',
    length_m: 10, width_m: 8, height_m: 2.8,
    client_name: '',
    variants: [
      { name: '', manufacturer: '', power_w: 36, lumens: 3600, cct_k: 4000, price: 0 },
      { name: '', manufacturer: '', power_w: 36, lumens: 3600, cct_k: 4000, price: 0 },
    ],
  });

  useEffect(function() { loadTasks(); loadAgentStatus(); }, [tenantId]);

  function loadTasks() {
    listDialuxTasks(tenantId, null, 20).then(function(data) {
      setTasks(data.tasks || []);
      setStats(data.stats || {});
    }).catch(function() {});
  }

  function loadAgentStatus() {
    getDialuxAgentStatus().then(function(data) {
      setAgentStatus(data);
    }).catch(function() {});
  }

  function handleCreateTask() {
    setLoading(true);
    createDialuxTask(form, tenantId).then(function(result) {
      toast.success(t(lang, 'Zadanie utworzone: ' + result.task_id, 'Task created: ' + result.task_id));
      setShowNew(false);
      loadTasks();
    }).catch(function(e) {
      toast.error(e.message || 'Error');
    }).finally(function() { setLoading(false); });
  }

  function handleCreateComparison() {
    setLoading(true);
    var data = {
      room_type: compareForm.room_type,
      room_name: compareForm.room_name,
      length_m: compareForm.length_m,
      width_m: compareForm.width_m,
      height_m: compareForm.height_m,
      client_name: compareForm.client_name,
      variants: compareForm.variants.filter(function(v) { return v.name; }),
    };
    createDialuxComparison(data, tenantId).then(function(result) {
      toast.success(t(lang, 'Porownanie utworzone: ' + result.comparison_id, 'Comparison created: ' + result.comparison_id));
      setShowCompare(false);
      loadTasks();
    }).catch(function(e) {
      toast.error(e.message || 'Error');
    }).finally(function() { setLoading(false); });
  }

  function addVariant() {
    setCompareForm(function(prev) {
      return Object.assign({}, prev, {
        variants: prev.variants.concat([{ name: '', manufacturer: '', power_w: 36, lumens: 3600, cct_k: 4000, price: 0 }])
      });
    });
  }

  var selectedRoom = ROOM_TYPES.find(function(r) { return r.key === form.room_type; });

  function StatusIcon({ status }) {
    if (status === 'completed') return React.createElement(CheckCircle, { className: 'w-4 h-4 text-green-500' });
    if (status === 'processing') return React.createElement(Loader2, { className: 'w-4 h-4 text-blue-400 animate-spin' });
    if (status === 'error') return React.createElement(XCircle, { className: 'w-4 h-4 text-red-500' });
    return React.createElement(Clock, { className: 'w-4 h-4 text-yellow-500' });
  }

  function StatusBadge({ status }) {
    var colors = {
      pending: 'bg-yellow-500/10 text-yellow-500',
      processing: 'bg-blue-500/10 text-blue-400',
      completed: 'bg-green-500/10 text-green-500',
      error: 'bg-red-500/10 text-red-500',
    };
    var labels = { pending: 'Oczekuje', processing: 'Obliczanie...', completed: 'Gotowy', error: 'Blad' };
    return React.createElement(Badge, { className: colors[status] || '' }, labels[status] || status);
  }

  return React.createElement('div', { className: 'space-y-4' },
    // Header
    React.createElement('div', { className: 'flex items-center justify-between' },
      React.createElement('div', null,
        React.createElement('h1', { className: 'text-2xl font-bold flex items-center gap-2' },
          React.createElement(Lightbulb, { className: 'w-6 h-6 text-primary' }),
          t(lang, 'Projekty DIALux', 'DIALux Projects')
        ),
        React.createElement('p', { className: 'text-muted-foreground text-sm' },
          t(lang, 'Automatyczne projekty oswietlenia z walidacja norm', 'Automated lighting projects with norm validation')
        )
      ),
      React.createElement('div', { className: 'flex items-center gap-2' },
        // Agent status
        React.createElement('div', { className: 'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs ' + (agentStatus.connected ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-400') },
          agentStatus.connected
            ? React.createElement(Wifi, { className: 'w-3.5 h-3.5' })
            : React.createElement(WifiOff, { className: 'w-3.5 h-3.5' }),
          agentStatus.connected
            ? t(lang, 'Agent polaczony', 'Agent connected')
            : t(lang, 'Agent offline', 'Agent offline')
        ),
        React.createElement(Button, { onClick: function() { setShowCompare(true); }, variant: 'outline', className: 'gap-1.5' },
          React.createElement(BarChart3, { className: 'w-4 h-4' }),
          t(lang, 'Porownaj warianty', 'Compare variants')
        ),
        React.createElement(Button, { onClick: function() { setShowNew(true); }, className: 'gap-1.5' },
          React.createElement(Plus, { className: 'w-4 h-4' }),
          t(lang, 'Nowy projekt', 'New project')
        )
      )
    ),

    // Stats
    React.createElement('div', { className: 'grid grid-cols-4 gap-3' },
      [
        { label: t(lang, 'Wszystkie', 'Total'), value: stats.total || 0, icon: FileText, color: 'text-primary' },
        { label: t(lang, 'Oczekujace', 'Pending'), value: stats.pending || 0, icon: Clock, color: 'text-yellow-500' },
        { label: t(lang, 'W toku', 'Processing'), value: stats.processing || 0, icon: Loader2, color: 'text-blue-400' },
        { label: t(lang, 'Gotowe', 'Completed'), value: stats.completed || 0, icon: CheckCircle, color: 'text-green-500' },
      ].map(function(s, i) {
        return React.createElement(Card, { key: i },
          React.createElement(CardContent, { className: 'pt-4 pb-4 flex items-center gap-3' },
            React.createElement(s.icon, { className: 'w-5 h-5 ' + s.color }),
            React.createElement('div', null,
              React.createElement('div', { className: 'text-2xl font-bold' }, s.value),
              React.createElement('div', { className: 'text-xs text-muted-foreground' }, s.label)
            )
          )
        );
      })
    ),

    // Task list
    React.createElement(Card, null,
      React.createElement(CardHeader, { className: 'pb-2 flex flex-row items-center justify-between' },
        React.createElement(CardTitle, { className: 'text-base' }, t(lang, 'Zadania', 'Tasks')),
        React.createElement(Button, { size: 'sm', variant: 'ghost', onClick: loadTasks, className: 'gap-1' },
          React.createElement(RefreshCw, { className: 'w-3.5 h-3.5' }),
          t(lang, 'Odswiez', 'Refresh')
        )
      ),
      React.createElement(CardContent, null,
        tasks.length === 0
          ? React.createElement('div', { className: 'text-center py-8 text-muted-foreground' },
              React.createElement(Lightbulb, { className: 'w-10 h-10 mx-auto mb-3 opacity-20' }),
              React.createElement('p', null, t(lang, 'Brak zadan. Kliknij "Nowy projekt" aby rozpoczac.', 'No tasks. Click "New project" to start.'))
            )
          : React.createElement('div', { className: 'space-y-2' },
              tasks.map(function(task) {
                var r = task.results || {};
                var v = task.validation || {};
                return React.createElement('div', {
                  key: task.task_id,
                  className: 'flex items-center justify-between px-4 py-3 border rounded-lg hover:border-primary/30 cursor-pointer transition-colors',
                  onClick: function() { setSelectedTask(task); }
                },
                  React.createElement('div', { className: 'flex items-center gap-3' },
                    React.createElement(StatusIcon, { status: task.status }),
                    React.createElement('div', null,
                      React.createElement('div', { className: 'font-medium text-sm' },
                        task.room_name || task.room_type,
                        task.comparison_id ? React.createElement(Badge, { variant: 'outline', className: 'text-xs ml-2' }, task.variant_name || 'Wariant') : null
                      ),
                      React.createElement('div', { className: 'text-xs text-muted-foreground flex items-center gap-2' },
                        React.createElement('span', null, task.length_m + 'x' + task.width_m + 'x' + task.height_m + 'm'),
                        task.luminaire_manufacturer && React.createElement('span', null, '| ' + task.luminaire_manufacturer),
                        task.client_name && React.createElement('span', null, '| ' + task.client_name)
                      )
                    )
                  ),
                  React.createElement('div', { className: 'flex items-center gap-3' },
                    task.status === 'completed' && React.createElement('div', { className: 'text-right text-xs' },
                      React.createElement('div', { className: 'font-mono font-medium ' + (v.passed ? 'text-green-500' : 'text-red-400') },
                        r.avg_lux ? r.avg_lux + ' lux' : ''
                      ),
                      React.createElement('div', { className: 'text-muted-foreground' },
                        v.passed ? 'Norma OK' : 'Niezgodny'
                      )
                    ),
                    React.createElement(StatusBadge, { status: task.status }),
                    React.createElement(ArrowRight, { className: 'w-4 h-4 text-muted-foreground' })
                  )
                );
              })
            )
      )
    ),

    // New Task Dialog
    showNew && React.createElement(Dialog, { open: true, onOpenChange: function() { setShowNew(false); } },
      React.createElement(DialogContent, { className: 'max-w-2xl max-h-[85vh] overflow-y-auto' },
        React.createElement(DialogHeader, null,
          React.createElement(DialogTitle, { className: 'flex items-center gap-2' },
            React.createElement(Lightbulb, { className: 'w-5 h-5 text-primary' }),
            t(lang, 'Nowy projekt oswietlenia', 'New lighting project')
          )
        ),
        React.createElement('div', { className: 'space-y-4' },
          // Room type
          React.createElement('div', null,
            React.createElement('label', { className: 'text-sm font-medium mb-1 block' }, t(lang, 'Typ pomieszczenia', 'Room type')),
            React.createElement('select', {
              className: 'w-full h-10 rounded-md border bg-background px-3 text-sm',
              value: form.room_type,
              onChange: function(e) { setForm(function(f) { return Object.assign({}, f, { room_type: e.target.value }); }); }
            },
              ROOM_TYPES.map(function(rt) {
                return React.createElement('option', { key: rt.key, value: rt.key },
                  rt.label + ' (' + rt.lux + ' lux, UGR<' + rt.ugr + ')'
                );
              })
            )
          ),
          // Room name + client
          React.createElement('div', { className: 'grid grid-cols-2 gap-3' },
            React.createElement('div', null,
              React.createElement('label', { className: 'text-sm font-medium mb-1 block' }, t(lang, 'Nazwa pomieszczenia', 'Room name')),
              React.createElement(Input, { value: form.room_name, onChange: function(e) { setForm(function(f) { return Object.assign({}, f, { room_name: e.target.value }); }); }, placeholder: 'np. Biuro A' })
            ),
            React.createElement('div', null,
              React.createElement('label', { className: 'text-sm font-medium mb-1 block' }, t(lang, 'Klient', 'Client')),
              React.createElement(Input, { value: form.client_name, onChange: function(e) { setForm(function(f) { return Object.assign({}, f, { client_name: e.target.value }); }); }, placeholder: 'ABC Sp. z o.o.' })
            )
          ),
          // Dimensions
          React.createElement('div', null,
            React.createElement('label', { className: 'text-sm font-medium mb-1 flex items-center gap-1' },
              React.createElement(Ruler, { className: 'w-3.5 h-3.5' }),
              t(lang, 'Wymiary (m)', 'Dimensions (m)')
            ),
            React.createElement('div', { className: 'grid grid-cols-3 gap-3' },
              React.createElement(Input, { type: 'number', step: '0.1', value: form.length_m, onChange: function(e) { setForm(function(f) { return Object.assign({}, f, { length_m: parseFloat(e.target.value) || 0 }); }); }, placeholder: 'Dlugosc' }),
              React.createElement(Input, { type: 'number', step: '0.1', value: form.width_m, onChange: function(e) { setForm(function(f) { return Object.assign({}, f, { width_m: parseFloat(e.target.value) || 0 }); }); }, placeholder: 'Szerokosc' }),
              React.createElement(Input, { type: 'number', step: '0.1', value: form.height_m, onChange: function(e) { setForm(function(f) { return Object.assign({}, f, { height_m: parseFloat(e.target.value) || 0 }); }); }, placeholder: 'Wysokosc' })
            ),
            React.createElement('div', { className: 'text-xs text-muted-foreground mt-1' },
              t(lang, 'Powierzchnia: ', 'Area: ') + (form.length_m * form.width_m).toFixed(1) + ' m2' +
              (selectedRoom ? ' | Norma: ' + selectedRoom.lux + ' lux, UGR<' + selectedRoom.ugr : '')
            )
          ),
          // Luminaire
          React.createElement('div', null,
            React.createElement('label', { className: 'text-sm font-medium mb-1 flex items-center gap-1' },
              React.createElement(Lightbulb, { className: 'w-3.5 h-3.5' }),
              t(lang, 'Oprawa oswietleniowa', 'Luminaire')
            ),
            React.createElement('div', { className: 'grid grid-cols-2 gap-3' },
              React.createElement(Input, { value: form.luminaire_name, onChange: function(e) { setForm(function(f) { return Object.assign({}, f, { luminaire_name: e.target.value }); }); }, placeholder: 'np. CoreLine Panel RC132V' }),
              React.createElement(Input, { value: form.luminaire_manufacturer, onChange: function(e) { setForm(function(f) { return Object.assign({}, f, { luminaire_manufacturer: e.target.value }); }); }, placeholder: 'Producent' })
            ),
            React.createElement('div', { className: 'grid grid-cols-3 gap-3 mt-2' },
              React.createElement('div', null,
                React.createElement('label', { className: 'text-xs text-muted-foreground' }, 'Moc (W)'),
                React.createElement(Input, { type: 'number', value: form.luminaire_power_w, onChange: function(e) { setForm(function(f) { return Object.assign({}, f, { luminaire_power_w: parseFloat(e.target.value) || 0 }); }); } })
              ),
              React.createElement('div', null,
                React.createElement('label', { className: 'text-xs text-muted-foreground' }, 'Lumeny (lm)'),
                React.createElement(Input, { type: 'number', value: form.luminaire_lumens, onChange: function(e) { setForm(function(f) { return Object.assign({}, f, { luminaire_lumens: parseFloat(e.target.value) || 0 }); }); } })
              ),
              React.createElement('div', null,
                React.createElement('label', { className: 'text-xs text-muted-foreground' }, 'CCT (K)'),
                React.createElement('select', {
                  className: 'w-full h-9 rounded-md border bg-background px-3 text-sm',
                  value: form.luminaire_cct_k,
                  onChange: function(e) { setForm(function(f) { return Object.assign({}, f, { luminaire_cct_k: parseInt(e.target.value) }); }); }
                },
                  [2700, 3000, 3500, 4000, 5000, 6500].map(function(k) {
                    return React.createElement('option', { key: k, value: k }, k + 'K');
                  })
                )
              )
            )
          ),
          // Budget + Notes
          React.createElement('div', { className: 'grid grid-cols-2 gap-3' },
            React.createElement('div', null,
              React.createElement('label', { className: 'text-sm font-medium mb-1 block' }, t(lang, 'Budzet (PLN)', 'Budget (PLN)')),
              React.createElement(Input, { type: 'number', value: form.budget_pln, onChange: function(e) { setForm(function(f) { return Object.assign({}, f, { budget_pln: parseFloat(e.target.value) || 0 }); }); }, placeholder: '0 = bez limitu' })
            ),
            React.createElement('div', null,
              React.createElement('label', { className: 'text-sm font-medium mb-1 block' }, t(lang, 'Uwagi', 'Notes')),
              React.createElement(Input, { value: form.notes, onChange: function(e) { setForm(function(f) { return Object.assign({}, f, { notes: e.target.value }); }); }, placeholder: 'Dodatkowe wymagania...' })
            )
          ),
          // Submit
          React.createElement('div', { className: 'flex justify-end gap-2 pt-2' },
            React.createElement(Button, { variant: 'outline', onClick: function() { setShowNew(false); } }, t(lang, 'Anuluj', 'Cancel')),
            React.createElement(Button, { onClick: handleCreateTask, disabled: loading, className: 'gap-1.5' },
              loading ? React.createElement(Loader2, { className: 'w-4 h-4 animate-spin' }) : React.createElement(Zap, { className: 'w-4 h-4' }),
              t(lang, 'Utworz i oblicz', 'Create & calculate')
            )
          )
        )
      )
    ),

    // Task Detail Dialog
    selectedTask && React.createElement(Dialog, { open: true, onOpenChange: function() { setSelectedTask(null); } },
      React.createElement(DialogContent, { className: 'max-w-2xl max-h-[85vh] overflow-y-auto' },
        React.createElement(DialogHeader, null,
          React.createElement(DialogTitle, { className: 'flex items-center gap-2' },
            React.createElement(StatusIcon, { status: selectedTask.status }),
            selectedTask.room_name || selectedTask.room_type,
            React.createElement(StatusBadge, { status: selectedTask.status })
          )
        ),
        React.createElement('div', { className: 'space-y-4' },
          // Room info
          React.createElement(Card, { className: 'bg-muted/20' },
            React.createElement(CardContent, { className: 'pt-4 pb-4' },
              React.createElement('div', { className: 'grid grid-cols-3 gap-4 text-sm' },
                React.createElement('div', null,
                  React.createElement('div', { className: 'text-muted-foreground text-xs' }, 'Wymiary'),
                  React.createElement('div', { className: 'font-medium' }, selectedTask.length_m + ' x ' + selectedTask.width_m + ' x ' + selectedTask.height_m + ' m')
                ),
                React.createElement('div', null,
                  React.createElement('div', { className: 'text-muted-foreground text-xs' }, 'Powierzchnia'),
                  React.createElement('div', { className: 'font-medium' }, (selectedTask.length_m * selectedTask.width_m).toFixed(1) + ' m2')
                ),
                React.createElement('div', null,
                  React.createElement('div', { className: 'text-muted-foreground text-xs' }, 'Oprawa'),
                  React.createElement('div', { className: 'font-medium' }, selectedTask.luminaire_name || '-')
                )
              )
            )
          ),
          // Results (if completed)
          selectedTask.status === 'completed' && selectedTask.results && React.createElement('div', null,
            React.createElement('h4', { className: 'text-sm font-semibold mb-2' }, 'Wyniki obliczen'),
            React.createElement('div', { className: 'grid grid-cols-2 md:grid-cols-4 gap-2' },
              [
                { label: 'Natezenie', value: (selectedTask.results.avg_lux || 0).toFixed(0), unit: 'lux', icon: Sun },
                { label: 'UGR', value: selectedTask.results.ugr || '-', unit: '', icon: Eye },
                { label: 'Rownomiernosc', value: (selectedTask.results.uniformity_u0 || 0).toFixed(2), unit: '', icon: BarChart3 },
                { label: 'Moc', value: (selectedTask.results.watts_per_m2 || 0).toFixed(1), unit: 'W/m2', icon: Zap },
              ].map(function(s, i) {
                return React.createElement('div', { key: i, className: 'bg-muted/30 rounded-lg p-3' },
                  React.createElement('div', { className: 'flex items-center gap-1.5 text-xs text-muted-foreground' },
                    React.createElement(s.icon, { className: 'w-3.5 h-3.5' }), s.label
                  ),
                  React.createElement('div', { className: 'text-lg font-bold mt-1' }, s.value, ' ', React.createElement('span', { className: 'text-sm font-normal' }, s.unit))
                );
              })
            )
          ),
          // Validation
          selectedTask.validation && React.createElement(Card, {
            className: selectedTask.validation.passed ? 'bg-green-500/5 border-green-500/20' : 'bg-red-500/5 border-red-500/20'
          },
            React.createElement(CardContent, { className: 'pt-4 pb-4' },
              React.createElement('div', { className: 'flex items-center gap-2 mb-2' },
                selectedTask.validation.passed
                  ? React.createElement(CheckCircle, { className: 'w-5 h-5 text-green-500' })
                  : React.createElement(AlertTriangle, { className: 'w-5 h-5 text-red-400' }),
                React.createElement('span', { className: 'font-medium' },
                  selectedTask.validation.passed ? 'ZGODNY z norma' : 'NIEZGODNY z norma'
                ),
                React.createElement(Badge, { variant: 'outline', className: 'text-xs ml-2' }, selectedTask.validation.standard || 'PN-EN 12464-1')
              ),
              selectedTask.validation.issues && selectedTask.validation.issues.length > 0 &&
                React.createElement('ul', { className: 'text-sm text-red-400 space-y-1 mt-2' },
                  selectedTask.validation.issues.map(function(issue, i) {
                    return React.createElement('li', { key: i, className: 'flex items-center gap-1' },
                      React.createElement(XCircle, { className: 'w-3.5 h-3.5 shrink-0' }), issue
                    );
                  })
                )
            )
          ),
          // Task info
          React.createElement('div', { className: 'text-xs text-muted-foreground border-t pt-3 flex justify-between' },
            React.createElement('span', null, 'ID: ' + selectedTask.task_id),
            React.createElement('span', null, selectedTask.created_at ? new Date(selectedTask.created_at).toLocaleString('pl') : '')
          )
        )
      )
    )
  );
}
